import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import {
  AnimatedToastStack,
  useAnimatedToastStack,
  type ToastStatus,
} from '@/components/ui/beui';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

const DURATION = 3200;

const VARIANT_STATUS: Record<ToastVariant, ToastStatus> = {
  success: 'success',
  error: 'error',
  info: 'info',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { toasts, showToast, dismissToast } = useAnimatedToastStack({
    defaultDuration: DURATION,
  });

  const push = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      showToast({ title: message, status: VARIANT_STATUS[variant] });
    },
    [showToast]
  );

  const api = useMemo<ToastContextValue>(
    () => ({
      toast: push,
      success: (m: string) => push(m, 'success'),
      error: (m: string) => push(m, 'error'),
      info: (m: string) => push(m, 'info'),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <AnimatedToastStack
        toasts={toasts}
        onDismiss={dismissToast}
        placement="fixed"
        position="bottom-right"
        portal
        className="z-[100] max-w-[360px]"
      />
    </ToastContext.Provider>
  );
}
